// ForecastGrid.jsx

import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Checkbox, FormControl, InputLabel, ListItemText, MenuItem, Select, Typography } from "@mui/material";
import { Construction, ImportExport, PushPin } from "@mui/icons-material";
import CollapsibleGridCard from "../../../components/CollapsibleGridCard";
import useRevenueLinesFlatColumns from "../../../lib/TableColumns/Modules/RevenueLinesFlatColumns";
import { GRID_SESSION_KEYS } from "../../../lib/gridSessionState";
import { gridFilteredSortedRowIdsSelector, GridFooterContainer, GridPagination, gridRowsLookupSelector, useGridApiContext, useGridSelector } from "@mui/x-data-grid";
import { useGridApiRef } from "@mui/x-data-grid-pro";

const ALL_FILTER_VALUE = "All";

const PRODUCT_GROUP_OPTIONS = [
    ALL_FILTER_VALUE,
    "Buyout",
    "Components",
    "Equipment",
    "Installation",
    "Service",
    "Dryers",
    "Spare Parts",
];

const MONTH_OPTIONS = [
    ALL_FILTER_VALUE,
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

const BOOKED_ORDERS_STAGE_OPTIONS = [
    ALL_FILTER_VALUE,
    "01 - Assign Resources",
    "02 - Submittals Sent",
    "03 - Submittals Approved",
    "04 - Shipped",
    "05 - Installed",
    "06 - Commissioned",
    "07 - Revenue Taken",
    "10 - Closed",
];

const getShipDateParts = (shipDate) => {
    if (!shipDate) return null;

    const date = new Date(shipDate);
    if (Number.isNaN(date.getTime())) return null;

    const [year, month] = date.toISOString().split("T")[0].split("-").map(Number);
    return { year, monthIndex: month - 1 };
};

const getShipYear = (shipDate) => {
    return getShipDateParts(shipDate)?.year ?? null;
};

const getShipMonth = (shipDate) => {
    const monthIndex = getShipDateParts(shipDate)?.monthIndex;
    return typeof monthIndex === "number" ? MONTH_OPTIONS[monthIndex + 1] : null;
};

const normalizeMultiSelectFilterValue = (rawValue, previousValue, normalizeValue = (value) => value) => {
    const selectedValues = Array.isArray(rawValue) ? rawValue : String(rawValue).split(",");
    const normalizedValues = selectedValues
        .map((value) => value === ALL_FILTER_VALUE ? ALL_FILTER_VALUE : normalizeValue(value))
        .filter((value) => value === ALL_FILTER_VALUE || (value !== "" && value !== null && typeof value !== "undefined"));
    const selectedAll = normalizedValues.includes(ALL_FILTER_VALUE);

    if (selectedAll && !previousValue.includes(ALL_FILTER_VALUE)) {
        return [ALL_FILTER_VALUE];
    }

    const selectedOptions = normalizedValues.filter((value) => value !== ALL_FILTER_VALUE);
    return selectedOptions.length ? selectedOptions : [ALL_FILTER_VALUE];
};

const normalizeYearFilterValue = (value) => {
    const numericValue = Number(value);
    return Number.isNaN(numericValue) ? null : numericValue;
};

const renderSelectedFilterValue = (selected) => {
    if (selected.includes(ALL_FILTER_VALUE)) return ALL_FILTER_VALUE;
    if (selected.length === 1) return selected[0];
    return `${selected.length} selected`;
};

function TotalsFooter() {
    const apiRef = useGridApiContext();
    const filteredIds = useGridSelector(apiRef, gridFilteredSortedRowIdsSelector);
    const rowsLookup = useGridSelector(apiRef, gridRowsLookupSelector);
    const visibleRows = filteredIds.map((id) => rowsLookup[id]);

    const totalRevenue = visibleRows.reduce((s, r) => s + (Number(r?.weightedAmount) || 0), 0);
    const totalMargin  = visibleRows.reduce((s, r) => s + (Number(r?.marginAmount) || 0), 0);
    const totalNonWeightedRevenue  = visibleRows.reduce((s, r) => s + (Number(r?.amount) || 0), 0);
    const fmt = (n) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });

    return (
        <GridFooterContainer sx={{ px: 2, py: 1, gap: 2, alignItems: "center", borderTop: "1px solid #e0e0e0", backgroundColor: "#f9fafb", display: "flex" }}>
            <Box sx={{ display: "flex", gap: 4 }}>
                <Typography variant="subtitle2" color={"#d21919"}><strong>Total Revenue (Non Weighted):</strong> {fmt(totalNonWeightedRevenue)}</Typography>
                <Typography variant="subtitle2" color={"#1976d2"}><strong>Total Revenue:</strong> {fmt(totalRevenue)}</Typography>
                <Typography variant="subtitle2" color={"#9c27b0"}><strong>Total Margin:</strong> {fmt(totalMargin)}</Typography>
            </Box>
            <Box sx={{ flex: 1 }} />
            <GridPagination />
        </GridFooterContainer>
    );
}

const ForecastGrid = ({ lines, activeLines, setActiveLines, years, handleOpenModal, handleCloseout, selectedRows, handleSelectionChange, sessionUserId }) => {
    const [filterYears, setFilterYears] = useState([new Date().getFullYear()]);
    const [productGroupFilter, setProductGroupFilter] = useState([ALL_FILTER_VALUE]);
    const [bookedOrdersStageFilter, setBookedOrdersStageFilter] = useState([ALL_FILTER_VALUE]);
    const [shipMonthFilter, setShipMonthFilter] = useState([ALL_FILTER_VALUE]);
    const columns = useRevenueLinesFlatColumns();
    const apiRef = useGridApiRef();

    const yearOptions = useMemo(() => [
        ...new Set([new Date().getFullYear(), ...years]),
    ].sort((a, b) => a - b), [years]);

    useEffect(() => {
        const includesAllYears = filterYears.includes(ALL_FILTER_VALUE);
        const includesAllProductGroups = productGroupFilter.includes(ALL_FILTER_VALUE);
        const includesAllBookedStages = bookedOrdersStageFilter.includes(ALL_FILTER_VALUE);
        const includesAllShipMonths = shipMonthFilter.includes(ALL_FILTER_VALUE);
        const filteredLines = lines.filter(
            (item) => {
                const shipYear = getShipYear(item.shipDate);
                const shipMonth = getShipMonth(item.shipDate);

                return (
                    (includesAllYears || filterYears.includes(shipYear)) &&
                    (includesAllProductGroups || productGroupFilter.includes(item.productGroup)) &&
                    (includesAllBookedStages || bookedOrdersStageFilter.includes(item.bookedOrdersStage)) &&
                    (includesAllShipMonths || shipMonthFilter.includes(shipMonth))
                );
            }
        );

        setActiveLines(filteredLines);
    }, [bookedOrdersStageFilter, filterYears, lines, productGroupFilter, setActiveLines, shipMonthFilter]);

    const handleYearFilterChange = useCallback((event) => {
        setFilterYears((previousValue) => normalizeMultiSelectFilterValue(event.target.value, previousValue, normalizeYearFilterValue));
    }, []);

    const handleProductGroupFilterChange = useCallback((event) => {
        setProductGroupFilter((previousValue) => normalizeMultiSelectFilterValue(event.target.value, previousValue));
    }, []);

    const handleBookedOrdersStageFilterChange = useCallback((event) => {
        setBookedOrdersStageFilter((previousValue) => normalizeMultiSelectFilterValue(event.target.value, previousValue));
    }, []);

    const handleShipMonthFilterChange = useCallback((event) => {
        setShipMonthFilter((previousValue) => normalizeMultiSelectFilterValue(event.target.value, previousValue));
    }, []);

    const handleExport = useCallback(() => {
        apiRef.current.exportDataAsCsv({
            fileName: `Revenue Lines Allowed In Forecast`
        })
    }, [apiRef]);

    const actionButtons = useMemo(() => [
        { title: "Update Selected Revenue Line(s)", onClick: handleOpenModal, icon: Construction, color: "primary" },
        { title: "Closeout Forecast", onClick: handleCloseout, icon: PushPin, color: "secondary" },
        { title: "Export To Excel", onClick: handleExport, icon: ImportExport, color: "success"}
    ], [handleCloseout, handleExport, handleOpenModal]);

    const filterNode = useMemo(() => (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <FormControl size="small" sx={{ minWidth: 96 }}>
                <InputLabel id="forecast-filter-year-label">Filter Year</InputLabel>
                <Select
                    labelId="forecast-filter-year-label"
                    id="forecast-filter-year"
                    multiple
                    value={filterYears}
                    label="Filter Year"
                    onChange={handleYearFilterChange}
                    renderValue={renderSelectedFilterValue}
                >
                    <MenuItem value={ALL_FILTER_VALUE}>
                        <Checkbox checked={filterYears.includes(ALL_FILTER_VALUE)} />
                        <ListItemText primary={ALL_FILTER_VALUE} />
                    </MenuItem>
                    {yearOptions.map((y) => (
                        <MenuItem key={y} value={y}>
                            <Checkbox checked={filterYears.includes(y)} />
                            <ListItemText primary={y} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel id="forecast-ship-month-filter-label">Ship Month</InputLabel>
                <Select
                    labelId="forecast-ship-month-filter-label"
                    id="forecast-ship-month-filter"
                    multiple
                    value={shipMonthFilter}
                    label="Ship Month"
                    onChange={handleShipMonthFilterChange}
                    renderValue={renderSelectedFilterValue}
                >
                    {MONTH_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                            <Checkbox checked={shipMonthFilter.includes(option)} />
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 160 }}>
                <InputLabel id="forecast-product-group-filter-label">Product Group</InputLabel>
                <Select
                    labelId="forecast-product-group-filter-label"
                    id="forecast-product-group-filter"
                    multiple
                    value={productGroupFilter}
                    label="Product Group"
                    onChange={handleProductGroupFilterChange}
                    renderValue={renderSelectedFilterValue}
                >
                    {PRODUCT_GROUP_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                            <Checkbox checked={productGroupFilter.includes(option)} />
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel id="forecast-booked-orders-stage-filter-label">Booked Orders Stage</InputLabel>
                <Select
                    labelId="forecast-booked-orders-stage-filter-label"
                    id="forecast-booked-orders-stage-filter"
                    multiple
                    value={bookedOrdersStageFilter}
                    label="Booked Orders Stage"
                    onChange={handleBookedOrdersStageFilterChange}
                    renderValue={renderSelectedFilterValue}
                >
                    {BOOKED_ORDERS_STAGE_OPTIONS.map((option) => (
                        <MenuItem key={option} value={option}>
                            <Checkbox checked={bookedOrdersStageFilter.includes(option)} />
                            <ListItemText primary={option} />
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    ), [
        bookedOrdersStageFilter,
        filterYears,
        handleBookedOrdersStageFilterChange,
        handleProductGroupFilterChange,
        handleShipMonthFilterChange,
        handleYearFilterChange,
        productGroupFilter,
        shipMonthFilter,
        yearOptions,
    ]);

    return (
        <CollapsibleGridCard
            title="Revenue Lines"
            linkLabel="Allowed In Forecast"
            linkHref="#"
            stickyHeader
            stickyHeaderTop={74}
            rows={activeLines}
            columns={columns}
            getRowId={(row) => row.lineId}
            checkboxSelection
            rowSelectionModel={selectedRows}
            onRowSelectionModelChange={handleSelectionChange}
            actionButtons={actionButtons}
            filterNode={filterNode}
            footerComponent={TotalsFooter} // this is the unique slots footer
            initialPageSize={25}
            pageSizeOptions={[25, 50, 100, 200, 300, 400, 500]}
            apiRef={apiRef}
            defaultSortModel={[{ field: "projectNumber", sort: "asc" }]}
            defaultVisibilityModel={ { amount: false } }
            sessionStorageKey={GRID_SESSION_KEYS.revenueLinesFlat}
            sessionUserId={sessionUserId}
        />
    );
};

export default memo(ForecastGrid);
